import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Lightbulb, BookOpen, Sparkle } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SupportHelpLine } from '@/components/SupportHelpLine';
import { QUICK_START_STEPS } from '@/lib/quickStartSteps';
import { WHATS_NEW, UPDATES_ENABLED } from '@/lib/whatsNew';

const SEEN_PREFIX = 'smsuite_qs_seen_';

export function useQuickStartAutoOpen(key, enabled, delay = 600) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!enabled || !key || localStorage.getItem(SEEN_PREFIX + key)) return;
    const t = setTimeout(() => setOpen(true), delay);
    return () => clearTimeout(t);
  }, [key, enabled, delay]);
  return [open, setOpen];
}

export const markQuickStartSeen = (key) => localStorage.setItem(SEEN_PREFIX + key, '1');

const UPDATES_KEY = 'smsuite_updates_seen';
export const hasUnseenUpdates = () => UPDATES_ENABLED && !!WHATS_NEW[0] && localStorage.getItem(UPDATES_KEY) !== WHATS_NEW[0].id;
export const markUpdatesSeen = () => WHATS_NEW[0] && localStorage.setItem(UPDATES_KEY, WHATS_NEW[0].id);

export function UpdatesList() {
  return (
    <div className="space-y-4" data-testid="qs-updates-list">
      {WHATS_NEW.map(u => (
        <div key={u.id} className="rounded-xl border border-[#E4E4E7] p-4" data-testid={`qs-update-${u.id}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkle size={16} weight="fill" className="text-[#F97316]" />
            <h4 className="text-sm font-bold text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }}>{u.title}</h4>
            <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-[#A1A1AA]">
              {new Date(u.date + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
          </div>
          <ul className="mt-2 space-y-1.5 pl-6 list-disc text-sm text-[#3F3F46] leading-relaxed">
            {u.points.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      ))}
    </div>
  );
}

export function StepCard({ step, index, onGo }) {
  const Icon = step.icon;
  return (
    <div data-testid={`qs-step-${step.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${step.color}1A`, color: step.color }}>
          <Icon size={22} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#F97316]">Step {index + 1} of {QUICK_START_STEPS.length}</div>
          <h3 className="text-lg font-bold tracking-tight text-[#0A0A0A]" style={{ fontFamily: 'var(--font-heading)' }}>{step.title}</h3>
          <p className="text-xs text-[#71717A]">Where: <span className="font-semibold text-[#3F3F46]">{step.where}</span></p>
        </div>
      </div>
      <ol className="mt-4 space-y-2.5">
        {step.points.map((p, i) => (
          <li key={i} className="flex gap-2.5 text-sm text-[#3F3F46] leading-relaxed">
            <span className="w-5 h-5 rounded-full bg-[#F4F4F5] text-[11px] font-bold text-[#0A0A0A] flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
            <span>{p}</span>
          </li>
        ))}
      </ol>
      <div className="mt-4 flex gap-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-3 text-xs text-[#92400E]">
        <Lightbulb size={16} weight="fill" className="shrink-0 text-[#F59E0B]" /> <span>{step.tip}</span>
      </div>
      {onGo && (
        <Button size="sm" variant="outline" className="mt-4 rounded-full" onClick={() => onGo(step.to)} data-testid={`qs-go-${step.id}`}>
          Open this page <ArrowRight size={14} className="ml-1.5" />
        </Button>
      )}
    </div>
  );
}

function StepList({ active, setActive }) {
  return (
    <nav className="hidden md:block w-56 shrink-0 border-r border-[#E4E4E7] bg-[#FAFAFA] p-3 overflow-y-auto" data-testid="qs-step-list">
      {QUICK_START_STEPS.map((s, i) => (
        <button
          key={s.id} type="button" onClick={() => setActive(i)}
          className={`w-full text-left flex items-center gap-2 rounded-lg px-2.5 py-2 text-xs transition-colors ${i === active ? 'bg-white shadow-sm font-semibold text-[#0A0A0A]' : 'text-[#52525B] hover:bg-white/70'}`}
          data-testid={`qs-nav-${s.id}`}
        >
          {i < active ? <CheckCircle size={15} weight="fill" className="text-[#10B981] shrink-0" />
            : <span className="w-[15px] text-center font-bold text-[#A1A1AA] shrink-0">{i + 1}</span>}
          <span className="truncate">{s.title}</span>
        </button>
      ))}
    </nav>
  );
}

export function QuickStartDialog({ open, onOpenChange, seenKey, inApp = false }) {
  const [active, setActive] = useState(0);
  const [dontShow, setDontShow] = useState(true);
  const [view, setView] = useState('steps');
  const showView = (v) => { setView(v); if (v === 'updates') markUpdatesSeen(); };
  const navigate = useNavigate();
  const last = QUICK_START_STEPS.length - 1;

  const close = () => {
    if (seenKey && dontShow) markQuickStartSeen(seenKey);
    onOpenChange(false);
  };
  const go = (to) => { close(); navigate(to); };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden" data-testid="quick-start-dialog">
        <div className="bg-[#0F172A] text-white px-6 py-4 flex items-center gap-3">
          <BookOpen size={24} weight="duotone" className="text-[#FDBA74]" />
          <div>
            <DialogTitle className="text-lg font-bold" style={{ fontFamily: 'var(--font-heading)' }}>Quick Start Guide</DialogTitle>
            <DialogDescription className="text-xs text-white/70">Set up SMSuitePro in {QUICK_START_STEPS.length} easy steps — from accounts and masters to daily billing.</DialogDescription>
          </div>
        </div>
        {UPDATES_ENABLED && <div className="flex gap-1 border-b border-[#E4E4E7] bg-[#F8FAFC] px-4 pt-2">
          {[['steps', 'Setup Steps'], ['updates', `New Updates (${WHATS_NEW.length})`]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => showView(k)}
              className={`rounded-t-lg px-4 py-2 text-xs font-bold transition-colors ${view === k ? 'bg-white text-[#0A0A0A] border border-b-white border-[#E4E4E7] -mb-px' : 'text-[#71717A] hover:text-[#0A0A0A]'}`}
              data-testid={`qs-tab-${k}`}>
              {k === 'updates' && <Sparkle size={12} weight="fill" className="inline mr-1 text-[#F97316]" />}{l}
            </button>
          ))}
        </div>}
        {view === 'steps' ? (
          <>
            <div className="h-1 bg-[#E4E4E7]"><div className="h-1 bg-[#F97316] transition-[width] duration-300" style={{ width: `${((active + 1) / QUICK_START_STEPS.length) * 100}%` }} /></div>
            <div className="flex h-[56vh] min-h-[340px]">
              <StepList active={active} setActive={setActive} />
              <div className="flex-1 overflow-y-auto p-6" key={active}>
                <div className="animate-in fade-in slide-in-from-right-2 duration-300">
                  <StepCard step={QUICK_START_STEPS[active]} index={active} onGo={inApp ? go : null} />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="h-[56vh] min-h-[340px] overflow-y-auto p-6"><UpdatesList /></div>
        )}
        <div className="border-t border-[#E4E4E7] px-4 sm:px-6 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <SupportHelpLine className="sm:!flex-row sm:!items-center" testid="qs-support" />
          <div className="sm:ml-auto flex items-center gap-2 flex-wrap">
            {seenKey && (
              <label className="flex items-center gap-1.5 text-xs text-[#52525B] mr-2 cursor-pointer">
                <Checkbox checked={dontShow} onCheckedChange={(v) => setDontShow(!!v)} data-testid="qs-dont-show" /> Don&apos;t show again
              </label>
            )}
            {view === 'steps' && <>
            <Button variant="outline" size="sm" disabled={active === 0} onClick={() => setActive(active - 1)} data-testid="qs-prev">
              <ArrowLeft size={14} className="mr-1" /> Back
            </Button>
            {active < last ? (
              <Button size="sm" className="bg-[#0F172A] hover:bg-[#1E293B]" onClick={() => setActive(active + 1)} data-testid="qs-next">
                Next <ArrowRight size={14} className="ml-1" />
              </Button>
            ) : (
              <Button size="sm" className="bg-[#F97316] hover:bg-[#EA580C]" onClick={close} data-testid="qs-finish">Finish</Button>
            )}
            </>}
            {view === 'updates' && <Button size="sm" className="bg-[#0F172A] hover:bg-[#1E293B]" onClick={close} data-testid="qs-close-updates">Close</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
